import { RootState } from "@/redux/store";
import { ErrorType } from "@/types";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import { useToast } from "@/components/ui/use-toast";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { X, Plus, Minus } from "lucide-react";
import { Input } from "@/components/ui/input";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { useGetEmailTemplatesQuery, useUpdateInvoiceRemindersMutation } from "@/services/systemConfigApi";
import { InvoiceReminders, Reminder } from "@/types/system-setting/system-config";
import { LoadingButton } from "@/components/ui/common/loading-button";
interface WorkflowFormProps {
    isOpen: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    action: string;
    setAction: React.Dispatch<React.SetStateAction<"add" | "edit">>;
    refetch: () => void;
    editModeData: InvoiceReminders | undefined;
}


const WorkflowForm = ({
    isOpen,
    setOpen,
    action,
    setAction,
    editModeData,
    refetch,
}: WorkflowFormProps) => {
    const { toast } = useToast();
    const [updateInvoiceReminders, { isLoading, error, status }] = useUpdateInvoiceRemindersMutation();

    // Fix initial state type and structure
    const initialValues: InvoiceReminders = {
        reminders: [
            {
                id: 1,
                name: "Reminder 1",
                days: 0,
                trigger: "",
                email_template_id: 0,
                status: true
            },
        ],
    };

    const form = useForm<InvoiceReminders>({
        mode: "all",
        defaultValues: initialValues,
    });

    const {
        control,
        watch,
        register,
        handleSubmit,
        clearErrors,
        getValues,
        reset,
        setValue,
        formState: { isSubmitting, errors },
    } = form;
    const watcher = watch();

    const { data: emailTemplatesData } = useGetEmailTemplatesQuery({
        // query: query,
        query: '',
    })

    const emailTemplates = useMemo(() => {
        return Array.isArray(emailTemplatesData?.data) ? emailTemplatesData?.data : [];
        // return Array.isArray(emailTemplatesData) ? emailTemplatesData : [];
    }, [emailTemplatesData]);

    useEffect(() => {
        if (action == "edit") {
            const payload = { ...editModeData };
            reset(payload as InvoiceReminders);
            setReminders(payload.reminders || []); // Initialize reminders state with editModeData
        } else {
            reset(initialValues as InvoiceReminders);
            setReminders(initialValues.reminders); // Initialize reminders state with initial values
        }
    }, [action]);

    // Fix reminders state initialization
    const [reminders, setReminders] = useState<Reminder[]>([]);

    // Add error state for form fields
    const [fieldErrors, setFieldErrors] = useState<{ [key: number]: { [key: string]: string } }>({});

    // Fix onSubmit type and implementation
    const onSubmit = async (data: InvoiceReminders) => {
        // Validation function
        const validateReminders = () => {
            const errors: { [key: number]: { [key: string]: string } } = {};
            let isValid = true;

            const seenCombinations = new Set<string>();

            reminders.forEach((reminder, index) => {
                errors[index] = {};

                const key = `${reminder.days}-${reminder.trigger}`;
                if (seenCombinations.has(key)) {
                    errors[index].days = 'Reminders cannot have the same days and trigger';
                    errors[index].trigger = 'Reminders cannot have the same days and trigger';
                    isValid = false;
                } else {
                    seenCombinations.add(key);
                }

                if (!reminder.days || reminder.days <= 0) {
                    errors[index].days = 'Minimum day set is 1';
                    isValid = false;
                }

                if (Number(reminder.days) > 25) {
                    errors[index].days = 'Maximum day set is 25';
                    isValid = false;
                }

                if (!reminder.trigger) {
                    errors[index].trigger = 'Trigger is required';
                    isValid = false;
                }

                if (!reminder.email_template_id || reminder.email_template_id <= 0) {
                    errors[index].email_template_id = 'Email template is required';
                    isValid = false;
                }
            });

            // Reminder ordering check: later index must have fewer days
            for (let i = 0; i < reminders.length; i++) {
                const current = reminders[i];
                if (!current.days || current.days <= 0) continue;

                for (let j = i + 1; j < reminders.length; j++) {
                    const next = reminders[j];
                    if (!next.days || next.days <= 0) continue;

                    if (next.days > current.days) {
                        errors[j] = {
                            ...errors[j],
                            days: `Reminder ${j + 1} (${next.days} days) cannot be greater than Reminder ${i + 1} (${current.days} days)`
                        };
                        isValid = false;
                    }
                }
            }

            setFieldErrors(errors);
            return isValid;
        };


        if (!validateReminders()) {
            toast({
                variant: "destructive",
                title: "Validation Error",
                description: "Please fix all validation errors before saving"
            });
            return;
        }

        try {
            const payload: InvoiceReminders = {
                reminders: reminders.map(reminder => ({
                    ...reminder,
                    days: typeof reminder.days === 'string' ? parseInt(reminder.days) : reminder.days || 0,
                }))
            };

            const resp = await updateInvoiceReminders(payload).unwrap();
            if (resp) {
                toast({
                    variant: "success",
                    title: "Workflow updated successfully",
                });
                await refetch();
                handleClose();
                return resp;
            }
        } catch (error) {
            console.error("Error", { error });
            if (error && typeof error === "object" && "data" in error) {
                const typedError = error as ErrorType;
                toast({
                    variant: "destructive",
                    title: "Error in Submission",
                    description: `${typedError.data?.detail || typedError.data?.message}`,
                });
                return false;
            } else {
                toast({
                    variant: "destructive",
                    title: "Error in Submission",
                    description: `Something Went Wrong.`,
                });
                return false;
            }
        }
    };

    const onError = () => {
        toast({
            variant: "destructive",
            description: "Please fill all the mandatory fields",
        });
    };

    const handleClose = () => {
        clearErrors();
        setAction("add");
        reset(
            initialValues, // Reset the form to its initial values
            {
                keepIsSubmitted: false, // Reset the form's submission state
                keepSubmitCount: false, // Reset the count of submissions
                keepDirtyValues: false, // Clear any dirty values (set to false to reset all fields)
                keepDefaultValues: true, // Keep default values as provided by initialValue
            }
        );

        setOpen(false);
    };


    // Add a new reminder
    const addReminder = () => {
        const newId = reminders.length > 0 ? Math.max(...reminders.map((r) => r.id || 0)) + 1 : 1
        setReminders([
            ...reminders,
            {
                id: newId,
                name: `Reminder ${newId}`,
                days: undefined,
                email_template_id: 0,
                trigger: "",
                status: true,
            },
        ])
    }

    // Remove a reminder
    const removeReminder = (index: number) => {
        if (reminders.length <= 1) return // Prevent removing all reminders
        setReminders(reminders.filter((reminder, i) => i !== index))
    }

    // Modify updateReminder to validate fields
    const updateReminder = (index: number, field: keyof Reminder, value: string | number) => {
        // Clear error for this field
        setFieldErrors(prev => ({
            ...prev,
            [index]: {
                ...(prev[index] || {}),
                [field]: ''
            }
        }))

        setReminders(reminders.map((reminder, i) => {
            if (i === index) {
                if (field === 'days') {
                    return {
                        ...reminder,
                        [field]: value === '' ? 0 : parseInt(value as string)
                    };
                }
                return { ...reminder, [field]: value };
            }
            return reminder;
        }))
    }

    console.log({ watcher, errors, action });
    return (
        <Sheet open={isOpen} onOpenChange={handleClose}>
            <SheetContent
                hideCloseButton
                className="bg-formbg w-full lg:!max-w-[900px] p-0 custom-scrollbar h-full "
            >
                <FormProvider {...form}>
                    <form
                        key={action}
                        noValidate
                        className="pb-4 "
                        onSubmit={handleSubmit(onSubmit, onError)}
                    >
                        <SheetHeader className="px-6 sticky top-0 z-40 py-3 bg-white shadow-sm">
                            <SheetTitle>
                                <div className="flex items-center justify-between flex-col sm:flex-row gap-3 sm:gap-5 bg-white">
                                    <div className="flex items-center gap-2">
                                        <Button
                                            type="button"
                                            className="h-fit bg-none border-none hover:bg-none text-center flex items-center gap-2 p-0"
                                            variant={"outline"}
                                            disabled={isSubmitting}
                                            onClick={handleClose}
                                        >
                                            <i className="fa fa-xmark "></i>
                                        </Button>
                                        <p className="font-semibold">Edit Workflow</p>
                                    </div>

                                    <div className="flex items-center justify-end sm:justify-center space-x-3 sm:space-x-[20px] w-full sm:w-auto">

                                        <Button
                                            type="button"
                                            className="w-[100px] text-center flex items-center gap-2 border-primary"
                                            variant={"outline"}
                                            disabled={isSubmitting}
                                            onClick={handleClose}
                                        >
                                            <i className="fa fa-xmark "></i>
                                            Cancel
                                        </Button>
                                        <LoadingButton
                                            type="submit"
                                            loading={isSubmitting}
                                            disabled={isSubmitting}
                                            className="w-[110px] cursor-pointer bg-primary hover:bg-primary flex items-center gap-1"
                                        >
                                            {!isSubmitting &&
                                                <i className="fa-regular fa-floppy-disk text-base px-1"></i>
                                            }
                                            {action == "add" ? "Save" : "Update"}
                                        </LoadingButton>
                                    </div>
                                </div>
                            </SheetTitle>
                        </SheetHeader>
                        <Separator className=" h-[1px] rounded-full     " />

                        <SheetDescription className=" h-full  p-6  ">
                            <Card>
                                <CardContent className="p-4">

                                    {reminders.map((reminder, index) => (
                                        <div key={reminder.id} className="flex relative ">
                                            {/* First reminder: line only extends downward */}
                                            {index === 0 && reminders.length >= 1 && (
                                                <div className="absolute left-4 top-1/2 bottom-0 w-px bg-gray-200"></div>
                                            )}

                                            {/* Middle reminders: line extends both ways */}
                                            {index > 0 && index < reminders.length - 1 && (
                                                <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200"></div>
                                            )}

                                            {/* Last reminder: line only extends upward */}
                                            {index === reminders.length - 1 && index !== 0 && (
                                                <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200"></div>
                                            )}



                                            {/* Minus button with connection point */}
                                            <div className="relative mr-5 z-10">
                                                <div className="absolute left-4 top-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-gray-200 rounded-lg flex items-center justify-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => removeReminder(index)}
                                                        className="flex items-center justify-center w-full h-full"
                                                    >
                                                        <Minus className="h-4 w-4 text-gray-600" />
                                                    </button>
                                                </div>
                                            </div>


                                            {/* Horizontal line connecting to card */}
                                            <div className="absolute left-8 top-1/2 w-7 h-px bg-gray-200 -translate-y-px"></div>

                                            <div className="w-10"></div>

                                            {/* Reminder content */}
                                            <div className="flex-1 py-2 w-full">
                                                <Card className="p-4 shadow-sm">
                                                    <h2 className="text-base font-medium mb-4 border-b pb-2">Reminder {index + 1}</h2>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                                                        <div>
                                                            <label className="block text-sm text-gray-600 mb-2">Days</label>
                                                            <Input
                                                                type="number"
                                                                min="1"
                                                                max="25"
                                                                placeholder="Enter Days (1-25)"
                                                                value={reminder.days || ''}
                                                                onChange={(e) => updateReminder(
                                                                    index,
                                                                    "days",
                                                                    e.target.value
                                                                )}
                                                                className={fieldErrors[index]?.days ? "border-red-500" : ""}
                                                            />
                                                            {fieldErrors[index]?.days && (
                                                                <span className="text-red-500 text-xs mt-1">{fieldErrors[index].days}</span>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm text-gray-600 mb-2">Trigger</label>
                                                            <Select
                                                                value={reminder.trigger}
                                                                onValueChange={(value) => updateReminder(index, "trigger", value)}
                                                            >
                                                                <SelectTrigger className={fieldErrors[index]?.trigger ? "border-red-500" : ""}>
                                                                    <SelectValue placeholder="Select Trigger" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    <SelectItem value="before">Before the start of the new billing cycle.</SelectItem>
                                                                    <SelectItem value="after">After the start of a new billing cycle.</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                            {fieldErrors[index]?.trigger && (
                                                                <span className="text-red-500 text-xs mt-1">{fieldErrors[index].trigger}</span>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <label className="block text-sm text-gray-600 mb-2">Email Template</label>
                                                            <Select
                                                                value={reminder.email_template_id > 0 ? String(reminder.email_template_id) : undefined}
                                                                onValueChange={(value) => updateReminder(index, "email_template_id", parseInt(value))}
                                                            >
                                                                <SelectTrigger className={fieldErrors[index]?.email_template_id ? "border-red-500" : ""}>
                                                                    <SelectValue placeholder="Select Template" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {emailTemplates.map((template) => (
                                                                        <SelectItem key={template.id} value={String(template.id)}>
                                                                            {template.name}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                            {fieldErrors[index]?.email_template_id && (
                                                                <span className="text-red-500 text-xs mt-1">{fieldErrors[index].email_template_id}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Card>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Add Reminder button with vertical line connection */}
                                    <div className="flex relative">
                                        {/* Vertical line connecting to Add button */}
                                        {reminders.length > 0 && <div className="absolute left-4 top-0 h-8 w-px bg-gray-200"></div>}

                                        {/* Add button with green background */}
                                        <div className="z-10 mt-2 flex ">
                                            <button
                                                type="button"
                                                onClick={addReminder}
                                                disabled={reminders.length >= 5}
                                                title={reminders.length >= 5 ? "You can only add up to 5 reminders" : ""}
                                                className="cursor-pointer disabled:cursor-not-allowed bg-primary disabled:bg-gray-300 hover:bg-green-600 text-white rounded-md flex items-center justify-center w-8 h-8"
                                            >
                                                <Plus className="h-5 w-5" />
                                            </button>
                                            <span className="ml-2 inline-block mt-2 text-sm font-medium">Add Reminder</span>
                                        </div>
                                    </div>

                                </CardContent>
                            </Card>
                        </SheetDescription>
                    </form>
                </FormProvider>
            </SheetContent>
        </Sheet>
    );

}

export default WorkflowForm;